// src/utils/extractApiErrors.js

/**
 * Normaliza las claves del objeto a minúsculas (shallow, solo primer nivel).
 * Ej: { Message: "x", Data: { ... } } -> { message: "x", data: { ... } }
 */
function normalizeKeysShallow(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [String(k).toLowerCase(), v])
  );
}

/**
 * Extrae un arreglo de mensajes de error a partir de distintas formas de respuesta de API.
 * Soporta:
 * - { success, code, message, data }
 * - { Success, Code, Message, Data }  (mayúsculas iniciales)
 * - data como diccionario de validaciones por campo: { field: ["err1", "err2"] }
 * - data como string simple o arreglo de strings
 * - errores de red (sin response)
 */
export default function extractApiErrors(error) {
  const rawApiData = error?.response?.data ?? error?.backend ?? {};
  const apiData = normalizeKeysShallow(rawApiData);
  const errors = [];

  // 1) Validaciones por campo: data = { campo: ["msg1", "msg2"], otroCampo: "msgX" }
  if (
    apiData?.data &&
    typeof apiData.data === "object" &&
    !Array.isArray(apiData.data)
  ) {
    for (const key in apiData.data) {
      const value = apiData.data[key];
      if (!value) continue;

      if (Array.isArray(value)) {
        // p.ej. ["El nombre es requerido", "Debe tener al menos 3 caracteres"]
        errors.push(...value.filter((x) => typeof x === "string" && x.trim()));
      } else if (typeof value === "string") {
        errors.push(value);
      } else if (typeof value === "object") {
        // A veces viene un objeto con Message/Detail/etc.
        const vNorm = normalizeKeysShallow(value);
        if (typeof vNorm.message === "string" && vNorm.message.trim()) {
          errors.push(vNorm.message);
        }
      }
    }
  }
  // 2) data como arreglo de strings
  else if (Array.isArray(apiData?.data)) {
    errors.push(
      ...apiData.data.filter((x) => typeof x === "string" && x.trim())
    );
  }
  // 3) data como string
  else if (typeof apiData?.data === "string" && apiData.data.trim()) {
    errors.push(`❌ ${apiData.data}`);
  }

  // 4) Si aún no tenemos nada, intenta con message (minúsculas o normalizado)
  if (errors.length === 0) {
    if (typeof apiData?.message === "string" && apiData.message.trim()) {
      errors.push(`❌ ${apiData.message}`);
    }
    // 5) Si no vino response (error de red / timeout / CORS, etc.)
    else if (!error?.response && typeof error?.message === "string") {
      errors.push(`❌ ${error.message}`);
    } else {
      errors.push("❌ Error inesperado. Intenta de nuevo.");
    }
  }

  // Devuelve strings, sin falsy
  return errors.map((e) => String(e)).filter(Boolean);
}
